/**
 * Registry of every cached `useApi` read whose entry is invalidated from somewhere else.
 *
 * It lives in shared rather than next to the request that fills the entry: a cache key is a
 * contract between that request and every write that stales it, and those writes live in other
 * slices. Owning the key inside one feature would make every writer import that feature.
 */

type CacheMethod = "GET" | "POST";

/**
 * How wide the prefix reaches:
 * - `own` — only the entries of this exact url; nested paths survive;
 * - `subtree` — this url and everything below it.
 */
type CacheScope = "own" | "subtree";

interface CachedEndpoint {
  method: CacheMethod
  url: string
  scope: CacheScope
  /** Who clears it. Documentation, not a type — shared cannot reference features. */
  invalidatedBy: readonly string[]
}

export const CACHED_ENDPOINTS = {
  usageSummary: {
    method: "GET",
    url: "/llm-usage/summary",
    scope: "own",
    invalidatedBy: ["POST /email-management/get-preview"],
  },
  generations: {
    method: "GET",
    url: "/llm-usage/generations",
    // `own`: a finished run's detail (`/generations/{id}/prompt`) never changes, so its entry
    // must survive new rows being added to the list.
    scope: "own",
    invalidatedBy: ["POST /email-management/get-preview"],
  },
  brandsByUser: {
    method: "GET",
    url: "/email-management/get-brands-by-user",
    scope: "own",
    invalidatedBy: ["PUT /auth/user-accesses", "PUT /auth/brand-access"],
  },
  llmFeatures: {
    method: "GET",
    url: "/llm-usage/features",
    scope: "own",
    invalidatedBy: ["PUT /llm-settings"],
  },
  // `subtree`, unlike `llmFeatures` above: the roster lives at
  // `/llm-usage/features/{feature}/brands`, so the prefix has to reach below the list url. It
  // therefore also drops the list entry — which the same write invalidates anyway.
  llmFeatureBrands: {
    method: "GET",
    url: "/llm-usage/features",
    scope: "subtree",
    invalidatedBy: ["PUT /llm-settings"],
  },
  llmSettings: {
    method: "GET",
    url: "/llm-usage/settings",
    scope: "own",
    // The entry is keyed per `?feature=`, but the prefix stops at the url — so this drops every
    // feature's entry, not just the written one. Deliberate: the params serialisation is the
    // library's private business, and over-invalidating a settings GET costs one refetch.
    invalidatedBy: ["PUT /llm-settings"],
  },
  // The read is a POST with a filter body, like every other reporting endpoint in this app. It
  // sits one path segment below the write, so `own` is what keeps this prefix off anything else
  // under `/monthly-labor-cost`.
  monthlyLaborCostList: {
    method: "POST",
    url: "/monthly-labor-cost/list",
    scope: "own",
    invalidatedBy: ["POST /monthly-labor-cost"],
  },
  // The four profit-analysis reads. All of them carry `RETURN_CREDIT_VALUE`, derived from the
  // return-rate thresholds set in Brand Settings; the lower two additionally carry `TOTAL_LABOR_COST`, derived
  // from the monthly labor cost setting. Without these entries someone who edits either figure
  // keeps reading the old report for the rest of the cache window.
  //
  // `POST`, not `GET`: every profit-analysis read is a POST with a filter body, and the prefix
  // encodes the method.
  profitOverallAnalysis: {
    method: "POST",
    url: "/profit-analysis/overall-analysis",
    scope: "own",
    invalidatedBy: [
      "POST /brand-settings/return-rate-threshold",
      "POST /brand-settings/products/return-rate-threshold",
    ],
  },
  profitBrandAnalysis: {
    method: "POST",
    url: "/profit-analysis/brand-analysis",
    scope: "own",
    invalidatedBy: [
      "POST /brand-settings/return-rate-threshold",
      "POST /brand-settings/products/return-rate-threshold",
    ],
  },
  profitBrandProducts: {
    method: "POST",
    url: "/profit-analysis/brand-products-analysis",
    scope: "own",
    invalidatedBy: [
      "POST /monthly-labor-cost",
      "POST /brand-settings/return-rate-threshold",
      "POST /brand-settings/products/return-rate-threshold",
    ],
  },
  profitSkuAnalysis: {
    method: "POST",
    url: "/profit-analysis/sku-analysis",
    scope: "own",
    invalidatedBy: [
      "POST /monthly-labor-cost",
      "POST /brand-settings/return-rate-threshold",
      "POST /brand-settings/products/return-rate-threshold",
    ],
  },
  // Keyed per cause/status/page body, and the same incident sits in several of those entries at
  // once. The optimistic patch only rewrites the response on screen, so without this a page
  // that is left and reopened is served from a fresh entry still holding the old status.
  // `own`: `/incidents/chart` below it does not depend on status or reason.
  buyBoxIncidents: {
    method: "POST",
    url: "/buy-box-intelligence/incidents",
    scope: "own",
    invalidatedBy: [
      "POST /buy-box-intelligence/incidents/set-status",
      "POST /buy-box-intelligence/incidents/set-reason",
    ],
  },
  // The two MCP reads (`/mcp-usage/summary`, `/mcp-usage/calls`) are deliberately absent: they do
  // not opt into caching. Their figures move continuously — calls land, and Snowflake resolves
  // credits for calls that were already pending — so every read of them must reach the network.
} as const satisfies Record<string, CachedEndpoint>;

type CacheEntry = keyof typeof CACHED_ENDPOINTS;

/**
 * The only place the library's key format is written down. `cache: true` keys an entry as
 * `auto:METHOD:url:params:data`, so the trailing `:` — the url/params separator — is exactly
 * what cuts nested paths off a prefix.
 */
const toPrefix = ({ method, url, scope }: CachedEndpoint): string =>
  `auto:${method}:${url}${scope === "own" ? ":" : ""}`;

export const CACHE_PREFIXES = Object.fromEntries(
  Object.entries(CACHED_ENDPOINTS).map(([key, endpoint]) => [key, toPrefix(endpoint)]),
) as Record<CacheEntry, string>;
